import { Text } from '@react-spectrum/s2';
import CheckmarkCircle from '@react-spectrum/s2/icons/CheckmarkCircle';
import CloseCircle from '@react-spectrum/s2/icons/CloseCircle';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	clearStoredState,
	clearUrlParams,
	exchangeCodeForToken,
	extractCodeFromUrl,
	isSquareSandboxMode,
	redirectToSquareOAuth,
	validateState,
} from '@/api/SquareApi';
import { logger } from '@/logger';
import {
	OAUTH_MESSAGE_TYPE,
	type OAuthSuccessMessage,
} from '@/spaces/settings/oauthMessages';
import type { SquareConnection } from '@/spaces/settings/userSettingsTypes';

type AuthState =
	| { state: 'loading' }
	| { state: 'exchanging' }
	| { state: 'done' }
	| { state: 'failed'; error: string };

const sendSuccess = (connectionData: SquareConnection) => {
	if (!window.opener) return;
	const payload: OAuthSuccessMessage = {
		type: OAUTH_MESSAGE_TYPE,
		provider: 'square',
		status: 'success',
		connectionUpdated: true,
		connectionData,
	};
	window.opener.postMessage(payload, window.location.origin);
};

export const AuthSquare = () => {
	const [authState, setAuthState] = useState<AuthState>({ state: 'loading' });
	const hasRunRef = useRef(false);

	const handleSuccess = useCallback((connectionData: SquareConnection) => {
		setAuthState({ state: 'done' });
		sendSuccess(connectionData);
		setTimeout(() => {
			window.close();
		}, 2000);
	}, []);

	const handleError = useCallback((message: string) => {
		setAuthState({ state: 'failed', error: message });
	}, []);

	useEffect(() => {
		if (hasRunRef.current) return;
		hasRunRef.current = true;

		const run = async () => {
			const { code, state, error, errorDescription } = extractCodeFromUrl();

			if (error) {
				handleError(errorDescription || error);
				return;
			}

			if (!code) {
				logger.info('No code in URL, redirecting to Square OAuth');
				redirectToSquareOAuth();
				return;
			}

			if (!validateState(state)) {
				clearStoredState();
				handleError('Invalid or expired OAuth state. Please try again.');
				return;
			}
			clearStoredState();

			try {
				setAuthState({ state: 'exchanging' });

				const tokenData = await exchangeCodeForToken(code);
				clearUrlParams();

				if (!tokenData.persisted) {
					// Square has the authorization but we have no record of it, so the
					// merchant would go back to a screen that still says "not connected"
					// with nothing to act on. Saying so here is the only place they learn.
					logger.error('Square connection was not saved', {
						reason: tokenData.persist_reason,
					});
					// A retry only helps when the failure was transient. An unset base
					// URL or a caller token with no user id is a deployment or identity
					// problem, so "try again" sends the merchant round a loop that fails
					// the same way every time and they never contact anyone.
					const retryCannotHelp =
						tokenData.persist_reason === 'beacon_api_base_url_unset' ||
						tokenData.persist_reason === 'no_user_id';
					handleError(
						retryCannotHelp
							? 'Connected to Square, but we could not record the connection. Please contact support.'
							: 'Connected to Square, but we could not save the connection. Please try again.',
					);
					return;
				}

				const connectionData: SquareConnection = {
					square_token: tokenData.access_token,
					square_token_expires_at: tokenData.expires_at,
					square_merchant_id: tokenData.merchant_id,
					square_sandbox: isSquareSandboxMode(),
					connected_at: new Date().toISOString(),
					square_refresh_token: tokenData.refresh_token,
					square_refresh_token_expires_at:
						tokenData.refresh_token_expires_at ?? null,
				};

				handleSuccess(connectionData);
			} catch (err) {
				handleError(
					err instanceof Error
						? err.message
						: 'Failed to connect Square account',
				);
			}
		};

		void run();
	}, [handleError, handleSuccess]);

	return (
		<div className="inset-0 flex h-screen w-screen items-center justify-center">
			<div
				className="my-6 flex h-1/2 w-full max-w-4/5 flex-col items-center
					justify-center gap-4 rounded-xl border border-(--glass-border)
					bg-(--glass-bg) p-6 text-center"
			>
				{authState.state === 'loading' && (
					<div className="flex flex-col gap-4">
						<Text
							styles={style({
								color: 'heading',
								fontSize: 'heading-lg',
								fontWeight: 'bold',
							})}
						>
							Connecting to Square
						</Text>
						<Text
							styles={style({
								color: 'title',
								fontSize: 'title-xl',
							})}
						>
							Please wait...
						</Text>
					</div>
				)}

				{authState.state === 'exchanging' && (
					<div className="flex flex-col gap-4">
						<Text
							styles={style({
								color: 'heading',
								fontSize: 'heading-lg',
								fontWeight: 'bold',
							})}
						>
							Connecting to Square
						</Text>
						<Text
							styles={style({
								color: 'title',
								fontSize: 'title-xl',
							})}
						>
							Completing authorization...
						</Text>
					</div>
				)}

				{authState.state === 'done' && (
					<div className="flex flex-col gap-4">
						<div className="flex items-center justify-center gap-2">
							<CheckmarkCircle
								styles={iconStyle({ size: 'XL', color: 'positive' })}
							/>
							<Text
								styles={style({
									color: 'heading',
									fontSize: 'heading-lg',
									fontWeight: 'bold',
								})}
							>
								Success!
							</Text>
						</div>
						<Text
							UNSAFE_className="whitespace-pre-line"
							styles={style({
								color: 'title',
								fontSize: 'title-xl',
							})}
						>
							{`Square account connected successfully.
This window will close automatically...`}
						</Text>
					</div>
				)}

				{authState.state === 'failed' && (
					<div className="flex flex-col gap-4">
						<div className="flex items-center justify-center gap-2">
							<CloseCircle
								styles={iconStyle({ size: 'XL', color: 'negative' })}
							/>
							<Text
								styles={style({
									color: 'heading',
									fontSize: 'heading-lg',
									fontWeight: 'bold',
								})}
							>
								Something went wrong
							</Text>
						</div>
						<Text
							UNSAFE_className="whitespace-pre-line"
							styles={style({
								color: 'title',
								fontSize: 'title-xl',
							})}
						>
							{authState.error ?? 'Something went wrong.'}
						</Text>
					</div>
				)}
			</div>
		</div>
	);
};
